import { useEffect, useRef, useCallback } from "react";
import { notificationApi } from "../api/notification";
import type { Notification } from "../types/notification";

interface UseSSEOptions {
  onNotification?: (notification: Notification) => void;
  onError?: (error: Error) => void;
  onConnect?: () => void;
}

export const useSSE = (options: UseSSEOptions = {}) => {
  const connectionRef = useRef<{ close: () => void } | null>(null);
  // Expose connectionRef on window for notificationApi to access
  if (typeof window !== "undefined") {
    window.connectionRef = connectionRef;
  }
  const optionsRef = useRef(options);
  const isConnectingRef = useRef(false);
  const hasInitializedRef = useRef(false);

  // Always update options ref without triggering re-renders
  optionsRef.current = options;

  const connect = useCallback(async () => {
    // Prevent multiple simultaneous connections
    if (isConnectingRef.current || connectionRef.current) {
      console.log("Connection already exists or is being established");
      return;
    }

    isConnectingRef.current = true;

    try {
      console.log("Creating new SSE connection");
      const connection = await notificationApi.connectSSE(
        (notification: Notification) => {
          optionsRef.current.onNotification?.(notification);
        },
        (error: Error) => {
          console.error("SSE connection error:", error);
          optionsRef.current.onError?.(error);
          isConnectingRef.current = false;
        }
      );

      connectionRef.current = connection;
      isConnectingRef.current = false;
      optionsRef.current.onConnect?.();
      console.log("SSE connection established");
    } catch (error) {
      console.error("Error creating SSE connection:", error);
      isConnectingRef.current = false;
      optionsRef.current.onError?.(error as Error);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (connectionRef.current) {
      console.log("Disconnecting SSE");
      connectionRef.current.close();
      connectionRef.current = null;
    }
    isConnectingRef.current = false;
  }, []);

  // Use a stable effect that only runs once
  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;

    // Add a small delay to ensure all components are mounted
    const timer = setTimeout(() => {
      console.log("Initializing SSE connection...");
      connect();
    }, 100);

    return () => {
      clearTimeout(timer);
      disconnect();
    };
  }, []);

  return {
    connect,
    disconnect,
    isConnected: connectionRef.current !== null,
  };
};
